import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';

import { LoadingTable } from '@amzn/imdb-shared-meridian-components/components/DataTable/LoadingTable/LoadingTable';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';

import { ListsUserSummary } from '../../../../../trustAdmin-api/generated-src/api';
import TrustAdminApiFactory from '../../../../../trustAdmin-api/TrustAdminApiFactory';
import { RequestErrorCard } from '../../../../features/error/RequestErrorCard';
import ListSpamWorkflowDataTable from '../../../../features/ListSpamWorkflowDataTable/ListSpamWorkflowDataTable';
import { ListSpamWorkflowErrorTable } from '../../../../features/ListSpamWorkflowDataTable/ListSpamWorkflowErrorTable';

export interface ListSpamWorkflowResponseData {
    customerId: string;
}

export interface ListSpamWorkflowError {
    id: string;
    code: string;
    message: string;
}

const BATCH_SIZE = 100;

export const ListSpamWorkflowResults: React.FC = () => {
    const { state } = useLocation();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [data, setData] = useState<ListsUserSummary[]>([]);
    const [errorIds, setErrorIds] = useState<ListSpamWorkflowError[]>([]);
    const [error, setError] = useState<any>(null);

    useEffect(() => {
        if (!state?.listsOwners) {
            setError(new Error('No data given in location state'));
            return;
        }

        async function convertDataToRows() {
            setLoading(true);
            setMessage('Fetching users data');

            try {
                const customerIds = state.listsOwners.customerIds || [];

                if (customerIds.length > 0) {
                    const totalIds = customerIds.length;
                    const allUserSummaries: ListsUserSummary[] = [];

                    for (let i = 0; i < customerIds.length; i += BATCH_SIZE) {
                        const batch = customerIds.slice(i, i + BATCH_SIZE);

                        setMessage(`Processing ${i + batch.length} of ${totalIds} users...`);

                        const response = await TrustAdminApiFactory().bulkGetListsUserSummary.bulkGetListsUserSummary({
                            ids: batch
                        });

                        if (response.data) {
                            allUserSummaries.push(...response.data);
                        }
                    }

                    setData(allUserSummaries);
                }

                if (state.listsOwners.errors.length > 0) {
                    setErrorIds(state.listsOwners.errors);
                }
            } catch (e: any) {
                setError(e);
                setMessage('');
            } finally {
                setMessage('Successfully fetched users data');
                setLoading(false);
            }
        }

        convertDataToRows();
    }, [state]);

    if (error) {
        return <RequestErrorCard error={error} />;
    }

    return (
        <>
            {!loading && message && <Alert type='success'>{message}</Alert>}
            <Heading level={1}>List Spam Workflow Search Results</Heading>
            <Row spacingInset='300' backgroundColor='secondary'></Row>
            {loading && (
                <>
                    <LoadingTable message={message} />
                </>
            )}
            {!loading && data?.length > 0 && <ListSpamWorkflowDataTable users={data} />}
            {state?.listsOwners?.errors?.length > 0 && (
                <>
                    <Heading level={2}>Errors</Heading>
                    <ListSpamWorkflowErrorTable responseErrors={errorIds} />
                </>
            )}
        </>
    );
};
